import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { useUserPreferences } from './UserPreferencesContext'

interface BackgroundMusicContextValue {
  isEnabled: boolean

  /**
   * Plays a new audio file from the provided URL or resume playing the currently paused/stopped one.
   *
   * @param {string | null} trackUrl - If `null`, resume the currently selected track.
   */
  play: (trackUrl?: string | null) => void
  pause: () => void
  stop: () => void

  /**
   * Plays a one-shot sound effect. Respects the user's BGM mute preference.
   *
   * @param {string} url - The URL of the sound effect to play.
   */
  playSoundFx: (url: string) => void

  setIsPrefMute: (mute: boolean) => void

  isPlaying: boolean
  trackUrl: string | null
}

const initialContextValue: BackgroundMusicContextValue = {
  isEnabled: false,

  play: () => {},
  pause: () => {},
  stop: () => {},
  playSoundFx: () => {},

  setIsPrefMute: () => {},

  isPlaying: false,
  trackUrl: null
}

export const BackgroundMusicContext = createContext<BackgroundMusicContextValue>(initialContextValue)

const SFX_POOL_SIZE = 4

export const BackgroundMusicProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const [trackUrl, setTrackUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [isPrefMute, setIsPrefMute] = useState<boolean>(true) // True if user preference wants to mute music

  const [isMuted, setIsMuted] = useState<boolean>(true)

  const playerRef = useRef<ReactPlayer | null>(null)
  const userPreferences = useUserPreferences()

  // Sync background music preference from UserPreferencesContext
  useEffect(() => {
    if (!userPreferences.isPreferencesLoaded) {
      // Preferences not loaded yet - keep muted until we know user's preference
      setIsPrefMute(true)
      return
    }

    // Set mute state (inverse of enabled)
    setIsPrefMute(!userPreferences.preferenceA11yBgm)
  }, [userPreferences.isPreferencesLoaded, userPreferences.preferenceA11yBgm])

  const play = useCallback((url: string | null = null): void => {
    if (url === null) {
      if (trackUrl !== null) {
        setIsPlaying(true)
      }
    } else {
      if (url === trackUrl) {
        setIsPlaying(true)
      } else {
        setTrackUrl(url)
        setIsPlaying(true)
      }
    }
  }, [trackUrl])

  const pause = useCallback((): void => {
    setIsPlaying(false)
  }, [])

  const stop = useCallback((): void => {
    setIsPlaying(false)
    if (playerRef.current !== null) {
      playerRef.current.seekTo(0.0)
    }
  }, [])

  // One Audio element cannot play two overlapping one-shots — assigning src restarts
  // it. MCQ fires the GO sound and the wrong-answer sound from the same click
  // (DMVP-1026 / DMVP-1035), so hand each call the next element in a small pool.
  const sfxPoolRef = useRef<HTMLAudioElement[]>([])
  const sfxPoolIndexRef = useRef<number>(0)

  useEffect(() => {
    sfxPoolRef.current = Array.from({ length: SFX_POOL_SIZE }, () => new Audio())
    return () => {
      sfxPoolRef.current.forEach((audio) => audio.pause())
      sfxPoolRef.current = []
    }
  }, [])

  const playSoundFx = useCallback((url: string): void => {
    if (isPrefMute || !isEnabled || sfxPoolRef.current.length === 0) return
    const audio = sfxPoolRef.current[sfxPoolIndexRef.current]
    sfxPoolIndexRef.current = (sfxPoolIndexRef.current + 1) % sfxPoolRef.current.length
    audio.src = url
    audio.volume = 1.0
    void audio.play()
  }, [isPrefMute, isEnabled])

  // Modern browsers block autoplay of media files until there has been an initial user interaction.
  // This handler takes care of capturing that interaction and enabling the player.
  const handleClickCapture = useCallback(() => {
    setIsEnabled(true)
  }, [])

  useEffect(() => {
    // Wait for preferences to load before allowing music to play
    const muted = !isEnabled || !isPlaying || isPrefMute || !userPreferences.isPreferencesLoaded
    setIsMuted(muted)
  }, [isPlaying, isEnabled, isPrefMute, userPreferences.isPreferencesLoaded])

  return (
    <BackgroundMusicContext.Provider
      value={{
        isEnabled,

        play,
        pause,
        stop,
        playSoundFx,

        setIsPrefMute,

        isPlaying,
        trackUrl
      }}
    >
      <div
        onClickCapture={handleClickCapture}
      >
        {children}
        <ReactPlayer
          ref={playerRef}
          url={trackUrl ?? ''}
          playing={isEnabled && isPlaying && userPreferences.isPreferencesLoaded && !isPrefMute}
          muted={isMuted}
          volume={1.0}
          loop
          className='absolute hidden'
          config={{
            file: {
              attributes: { crossOrigin: 'true' },
              forceAudio: true
            }
          }}
        />
      </div>
    </BackgroundMusicContext.Provider>
  )
}

export const useBackgroundMusic = (): BackgroundMusicContextValue => {
  return useContext(BackgroundMusicContext)
}
